import { ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { Redirect } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

/** Column model of the redirects table; `meta` ties columns to the list's sorts and filters. */
export function useRedirectColumns() {
  const { t } = useTranslation('redirects');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<Redirect>>>(
    () => [
      {
        id: 'fromPath',
        header: t('columns.from'),
        cell: ({ row }) => (
          <span className="font-mono text-[13px] break-all">{row.original.fromPath}</span>
        ),
        meta: { label: t('columns.from'), sortField: 'fromPath', lockVisibility: true },
      },
      {
        id: 'toPath',
        header: t('columns.to'),
        cell: ({ row }) => (
          <span className="flex items-start gap-2">
            <ArrowRight className="text-muted-foreground mt-0.5 size-4 shrink-0" aria-hidden />
            <span className="font-mono text-[13px] break-all">{row.original.toPath}</span>
          </span>
        ),
        meta: { label: t('columns.to') },
      },
      {
        id: 'statusCode',
        header: t('columns.statusCode'),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            title={t(`statusCode.${row.original.statusCode === 302 ? '302' : '301'}`)}
          >
            {row.original.statusCode}
          </Badge>
        ),
        meta: { label: t('columns.statusCode'), filterKey: 'statusCode' },
      },
      {
        id: 'hits',
        header: t('columns.hits'),
        cell: ({ row }) => <span className="tabular-nums">{format.number(row.original.hits)}</span>,
        meta: { label: t('columns.hits'), sortField: 'hits', filterKey: 'hits', align: 'end' },
      },
      {
        id: 'lastHitAt',
        header: t('columns.lastHit'),
        cell: ({ row }) =>
          row.original.lastHitAt ? (
            <span title={format.utc(row.original.lastHitAt)} className="whitespace-nowrap">
              {format.relative(row.original.lastHitAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('columns.never')}</span>
          ),
        meta: { label: t('columns.lastHit'), sortField: 'lastHitAt', filterKey: 'lastHitAt' },
      },
    ],
    [format, t],
  );
}
